<template>
  <div v-if="open" class="ai-modal-backdrop" @click.self="$emit('close')">
    <div class="ai-modal">
      <div class="ai-modal-header">
        <h3><i class="ri-magic-line"></i> AI 生成背景圖</h3>
        <button type="button" class="ai-modal-close" @click="$emit('close')">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="ai-modal-body">
        <div v-if="quota" class="quota-bar">
          <i class="ri-coin-line"></i>
          剩餘餘額：<strong>NT$ {{ Number(quota.availableTwd || 0).toLocaleString() }}</strong>
          <span class="quota-detail">
            （本月免費 NT$ {{ Number(quota.freeTwdRemaining || 0).toLocaleString() }} / 儲值 NT$ {{ Number(quota.purchasedTwd || 0).toLocaleString() }}）
          </span>
        </div>

        <div class="form-group">
          <label class="form-label required-label">活動主題</label>
          <input
            v-model="form.theme"
            type="text"
            class="form-control"
            placeholder="例如：母親節優惠、夏季新品、會員專屬"
            maxlength="120"
          />
        </div>

        <div class="form-group">
          <label class="form-label">風格</label>
          <div class="style-picker">
            <button
              v-for="opt in styleOptions"
              :key="opt.value"
              type="button"
              :class="['style-btn', { active: form.style === opt.value }]"
              @click="form.style = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="form-row brand-row">
          <div class="form-group">
            <label class="form-label">主色（企業色）</label>
            <div class="color-field">
              <input v-model="form.brandColor" type="color" class="color-input" />
              <input v-model="form.brandColor" type="text" class="form-control hex-input" maxlength="7" />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">輔色</label>
            <div class="color-field">
              <input v-model="form.brandSecondaryColor" type="color" class="color-input" />
              <input v-model="form.brandSecondaryColor" type="text" class="form-control hex-input" maxlength="7" />
            </div>
          </div>
        </div>
        <p class="form-hint">
          色彩預設來自「系統參數 → AI 創意設定」；此處可針對本次生圖覆寫。
          <router-link to="/client/settings?tab=ai_creative" class="settings-link">前往設定</router-link>
        </p>

        <div class="form-group">
          <label class="form-label">按鈕區塊提示（選填）</label>
          <textarea
            v-model="form.buttonHints"
            class="form-control"
            rows="2"
            placeholder="例如：左側放主打商品視覺，右側三格放分類圖示區"
            maxlength="300"
          />
          <p class="form-hint">
            目前版型 {{ layoutType }} 格；AI 會依版型留出適合的視覺區塊（不含可讀文字）。
          </p>
          <p v-if="tabCount > 1" class="form-hint">
            多 Tab 模式：將預留上方
            <strong>{{ tabBarSize === 'slim' ? '細版（216px）' : '粗版（306px）' }}</strong>
            切換列空間。
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">區塊級提示（選填）</label>
          <div class="area-prompt-grid">
            <div
              v-for="(_, idx) in form.areaPrompts"
              :key="idx"
              class="area-prompt-item"
            >
              <label class="area-prompt-label">區塊 {{ idx + 1 }}</label>
              <input
                v-model="form.areaPrompts[idx]"
                type="text"
                class="form-control"
                :placeholder="`例如：${idx + 1}號區塊主打新品`"
                maxlength="60"
              />
            </div>
          </div>
          <p class="form-hint">可逐格指定視覺重點，讓生成結果更貼近每個按鈕區塊。</p>
        </div>

        <div class="form-group">
          <label class="form-label">按鈕辨識文字（選填）</label>
          <div class="area-prompt-grid">
            <div
              v-for="(_, idx) in form.areaTexts"
              :key="`text-${idx}`"
              class="area-prompt-item"
            >
              <label class="area-prompt-label">區塊 {{ idx + 1 }} 文字</label>
              <input
                v-model="form.areaTexts[idx]"
                type="text"
                class="form-control"
                :placeholder="`例如：立即領券`"
                maxlength="12"
              />
            </div>
          </div>
          <p class="form-hint">可回填到按鈕標籤供後台辨識；是否疊進圖片可由下方開關決定。</p>
        </div>

        <div class="form-group">
          <label class="toggle-option-row">
            <input v-model="form.includeTextInImage" type="checkbox" />
            <span>將按鈕文字一起疊進最終圖片</span>
          </label>
          <p class="form-hint">
            {{ form.includeTextInImage
              ? '已開啟：文字會燒進輸出圖片（LINE 端可直接看到）'
              : '已關閉：文字只用於後台辨識，不會出現在輸出圖片' }}
          </p>
        </div>

        <div v-if="previewUrl" class="preview-block">
          <label class="form-label">生成結果預覽</label>
          <img :src="previewUrl" alt="AI 生成預覽" class="preview-img" />
        </div>
      </div>

      <div class="ai-modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="generating" @click="$emit('close')">
          取消
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="generating || !form.theme.trim() || (quota && Number(quota.availableTwd || 0) < 1)"
          @click="handleGenerate"
        >
          <i :class="generating ? 'ri-loader-4-line spin' : 'ri-magic-line'"></i>
          {{ generating ? '生成中…' : '生成並套用（依 token 計費）' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { aiCreativeService, AI_CREATIVE_STYLES } from '@/api/modules/aiCreative.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'RichMenuAiGenerateModal',
  props: {
    open: { type: Boolean, default: false },
    heightType: { type: String, default: 'full' },
    layoutType: { type: String, default: '6' },
    tabName: { type: String, default: '' },
    tabNames: { type: Array, default: () => [] },
    tabCount: { type: Number, default: 1 },
    tabBarSize: { type: String, default: 'wide' },
    areaHints: { type: Array, default: () => [] },
  },
  emits: ['close', 'generated'],
  data() {
    return {
      generating: false,
      quota: null,
      previewUrl: '',
      styleOptions: AI_CREATIVE_STYLES,
      form: {
        theme: '',
        style: 'modern',
        brandColor: '#1a1a1a',
        brandSecondaryColor: '#f5a623',
        buttonHints: '',
        areaPrompts: [],
        areaTexts: [],
        includeTextInImage: false,
      },
    };
  },
  watch: {
    open(val) {
      if (val) {
        this.previewUrl = '';
        this.syncAreaPrompts();
        this.syncAreaTexts();
        this.loadInitialData();
      }
    },
    layoutType() {
      this.syncAreaPrompts();
      this.syncAreaTexts();
    },
  },
  methods: {
    getLayoutCount() {
      const count = Number.parseInt(this.layoutType, 10);
      return Number.isNaN(count) ? 6 : count;
    },
    syncAreaPrompts() {
      const count = this.getLayoutCount();
      const current = Array.isArray(this.form.areaPrompts) ? this.form.areaPrompts : [];
      this.form.areaPrompts = Array.from({ length: count }, (_, idx) => {
        const existing = typeof current[idx] === 'string' ? current[idx] : '';
        if (existing.trim()) return existing;
        const area = this.areaHints[idx];
        return typeof area?.label === 'string' ? area.label : '';
      });
    },
    syncAreaTexts() {
      const count = this.getLayoutCount();
      const current = Array.isArray(this.form.areaTexts) ? this.form.areaTexts : [];
      this.form.areaTexts = Array.from({ length: count }, (_, idx) => {
        const existing = typeof current[idx] === 'string' ? current[idx] : '';
        if (existing.trim()) return existing;
        const area = this.areaHints[idx];
        return typeof area?.label === 'string' ? area.label : '';
      });
    },
    async loadInitialData() {
      try {
        const [quotaRes, settingsRes, memberStyleRes] = await Promise.all([
          aiCreativeService.getQuota(),
          clientSettingsService.get(NAMESPACES.AI_CREATIVE).catch(() => null),
          clientSettingsService.get(NAMESPACES.MEMBER_HOME_STYLE).catch(() => null),
        ]);
        this.quota = quotaRes?.data ?? null;

        const aiSettings = settingsRes?.data?.settings ?? settingsRes?.data ?? {};
        const memberStyle = memberStyleRes?.data?.settings ?? memberStyleRes?.data ?? {};
        const syncFromMember = aiSettings.syncFromMemberHomeStyle !== false;

        this.form.brandColor = aiSettings.brandColor
          || (syncFromMember ? memberStyle.brandColor : null)
          || '#1a1a1a';
        this.form.brandSecondaryColor = aiSettings.brandSecondaryColor
          || (syncFromMember ? memberStyle.accentColor : null)
          || '#f5a623';
      } catch (e) {
        console.warn('載入 AI 生圖設定失敗', e);
      }
    },
    async handleGenerate() {
      if (!this.form.theme.trim()) return;
      this.generating = true;
      this.previewUrl = '';
      try {
        const res = await aiCreativeService.generateRichMenuImage({
          theme: this.form.theme.trim(),
          style: this.form.style,
          brandColor: this.form.brandColor,
          brandSecondaryColor: this.form.brandSecondaryColor,
          buttonHints: this.form.buttonHints.trim() || undefined,
          areaHints: this.form.areaPrompts.map(v => (v || '').trim()),
          areaTexts: this.form.areaTexts.map(v => (v || '').trim()),
          includeTextInImage: this.form.includeTextInImage === true,
          tabName: this.tabName,
          tabNames: this.tabNames,
          heightType: this.heightType,
          layoutType: this.layoutType,
          tabCount: this.tabCount,
          tabBarSize: this.tabBarSize,
        });
        const data = res?.data;
        if (!data?.imageUrl) {
          throw new Error('未取得圖片 URL');
        }
        this.previewUrl = data.imageUrl;
        if (data.quota) {
          this.quota = {
            ...this.quota,
            availableTwd: data.quota.availableTwd,
            freeTwdRemaining: data.quota.freeTwdRemaining,
            purchasedTwd: data.quota.purchasedTwd,
          };
        }
        await showSuccess('AI 背景圖已生成');
        this.$emit('generated', {
          imageUrl: data.imageUrl,
          areaTexts: this.form.areaTexts.map(v => (v || '').trim()),
        });
      } catch (e) {
        await showError(e?.response?.data?.message || e.message || 'AI 生圖失敗');
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.ai-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.ai-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.ai-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.ai-modal-header h3 {
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-modal-close {
  border: none;
  background: none;
  font-size: 22px;
  cursor: pointer;
  color: #666;
}

.ai-modal-body {
  padding: 16px 20px;
  overflow-y: auto;
}

.ai-modal-footer {
  padding: 12px 20px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.quota-bar {
  background: #f0f7ff;
  border: 1px solid #cce0ff;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #1a5490;
}

.quota-detail {
  color: #666;
  font-size: 12px;
  margin-left: 4px;
}

.style-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.style-btn {
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 20px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
}

.style-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.brand-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.color-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-input {
  width: 40px;
  height: 36px;
  padding: 2px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
}

.hex-input {
  flex: 1;
}

.form-hint {
  font-size: 12px;
  color: #888;
  margin: 6px 0 0;
}

.settings-link {
  color: #2563eb;
  text-decoration: none;
}

.preview-block {
  margin-top: 16px;
}

.preview-img {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #eee;
  max-height: 200px;
  object-fit: cover;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

.required-label::after {
  content: ' *';
  color: #e53e3e;
}

.form-control {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-group {
  margin-bottom: 14px;
}

.area-prompt-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.area-prompt-item {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 8px;
  background: #fafafa;
}

.area-prompt-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.toggle-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .brand-row {
    grid-template-columns: 1fr;
  }
  .area-prompt-grid {
    grid-template-columns: 1fr;
  }
}
</style>
